import { Cesium } from 'app/cesium';
import { useCallback, useContext, useState } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { useFarmPath } from 'app/agriculture/router/useFarmPath';
import { useNavigate } from 'react-router-dom';

export enum GlobeMat {
    None = 'none',
    Elevation = 'elevation',
    Aspect = 'aspect',
    Slope = 'slope',
}

const elevationMaterial: Cesium.Material = new Cesium.Material({
    fabric: {
        type: 'HeightColour',
        uniforms: {
            lowestElevationColour: new Cesium.Color(0.0, 0.0, 1.0, 1.0),
            highestElevationColor: new Cesium.Color(1.0, 0.0, 0.0, 1.0),
        },
        source: `
                                    czm_material czm_getMaterial(czm_materialInput materialInput)
                                    {
                                        czm_material m = czm_getDefaultMaterial(materialInput);                          
                                        float height = (materialInput.height + 414.0) / (8777.0 + 414.0);
                                        m.diffuse = mix(lowestElevationColour.rgb, highestElevationColor.rgb, height);  
                                        return m;
                                    }
                                `,
    },
});

const aspectMaterial: Cesium.Material = new Cesium.Material({
    fabric: {
        type: 'AspectColour',
        uniforms: {
            eastAspectColor: new Cesium.Color(0.0, 0.0, 1.0, 1.0),
            northAspectColor: new Cesium.Color(1.0, 0.0, 0.0, 1.0),
            westAspectColor: new Cesium.Color(0.0, 1.0, 0.0, 1.0),
            southAspectColor: new Cesium.Color(1.0, 1.0, 0.0, 1.0),
        },
        source: `
                                    czm_material czm_getMaterial(czm_materialInput materialInput)
                                    {
                                        czm_material m = czm_getDefaultMaterial(materialInput);                          
                                        float aspect = materialInput.aspect / (3.1415 * 2.0);
                                        if (aspect >= 0.0 && aspect < 0.25){
                                            m.diffuse = mix(eastAspectColor.rgb, northAspectColor.rgb, aspect / 0.25);  
                                        }
                                        if (aspect >= 0.25 && aspect < 0.50){
                                            m.diffuse = mix(northAspectColor.rgb, westAspectColor.rgb, ((aspect - 0.25) / 0.25));  
                                        }
                                        if (aspect >= 0.50 && aspect < 0.75){
                                            m.diffuse = mix(westAspectColor.rgb, southAspectColor.rgb, ((aspect - 0.50) / 0.25));  
                                        }
                                        if (aspect >= 0.75 && aspect <= 1.0){
                                            m.diffuse = mix(southAspectColor.rgb, eastAspectColor.rgb, ((aspect - 0.75) / 0.25));  
                                        }
                                        return m;
                                    }
                                `,
    },
});

const slopeMaterial: Cesium.Material = new Cesium.Material({
    fabric: {
        type: 'SlopeColour',
        uniforms: {
            lowestSlopeColor: new Cesium.Color(0.0, 1.0, 0.0, 1.0),
            lowSlopeColor: new Cesium.Color(0.0, 0.2, 0.0, 1.0),
            highSlopeColor: new Cesium.Color(0.2, 0.0, 0.0, 1.0),
            highestSlopeColor: new Cesium.Color(1.0, 0.0, 0.0, 1.0),
        },
        source: `
                                    czm_material czm_getMaterial(czm_materialInput materialInput)
                                    {
                                        czm_material m = czm_getDefaultMaterial(materialInput);
                                        
                                        float slope = materialInput.slope / (1.5708); // divide by PI/2
                                        
                                        if (slope >0.1763){ // 17.63% == 10 degrees
                                          m.diffuse = mix(highSlopeColor.rgb, highestSlopeColor.rgb, slope);
                                        }
                                        else {
                                          m.diffuse=mix(lowestSlopeColor.rgb, lowSlopeColor.rgb, slope/0.1763);
                                        }
                                        return m;
                                    }
                                `,
    },
});

export function useTerrainOverlay() {
    const [globeType, setGlobeType] = useState<GlobeMat>(GlobeMat.None);
    const { mapService, terrainService } = useContext(ServicesContext);
    const navigate = useNavigate();
    const farmPath = useFarmPath();

    const setGlobeTerrainMaterials = useCallback(
        (type: GlobeMat) => {
            const { viewer } = mapService;
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (viewer == null) return;
            const { globe } = viewer.scene;
            let material;

            switch (type) {
                case GlobeMat.Elevation:
                    material = elevationMaterial;
                    break;
                case GlobeMat.Aspect:
                    material = aspectMaterial;
                    break;
                case GlobeMat.Slope:
                    material = slopeMaterial;
                    break;
            }

            if (type !== GlobeMat.None) {
                navigate(farmPath(`/terrain-overlay-legend/${type}`));
                void terrainService.setMapTerrainEnabled(true);
            }
            setGlobeType(type);
            // eslint-disable-next-line react-compiler/react-compiler
            globe.material = material;
        },
        [farmPath, mapService, navigate, terrainService],
    );

    return {
        setGlobalMaterial: setGlobeTerrainMaterials,
        globeType,
    };
}
